"use client";

import { useEffect, useRef, useState } from "react";
import type { ReactElement } from "react";
import type { FunnelEmbedSlot } from "./developers-funnel-content";
import { YouTubeLogo } from "./developers-funnel-social";

/** Native pixel width of the architecture panel. */
const ARCH_W: number = 1068;

/**
 * Fits the fixed-pixel architecture HTML into the section column.
 * Grid would otherwise keep the iframe at 1208px and clip it.
 */
export function DevelopersArchitectureEmbed({
  slot,
  className,
}: {
  readonly slot: FunnelEmbedSlot;
  readonly className: string;
}): ReactElement {
  const boxRef = useRef<HTMLDivElement>(null);
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [frameReady, setFrameReady] = useState<boolean>(false);

  useEffect(() => {
    const box: HTMLDivElement | null = boxRef.current;
    if (!box) return;

    function applyScale(): void {
      const el: HTMLDivElement | null = boxRef.current;
      if (!el) return;
      el.style.setProperty("--dvf-arch-scale", String(el.clientWidth / ARCH_W));
    }

    applyScale();
    const observer: ResizeObserver = new ResizeObserver(applyScale);
    observer.observe(box);
    return () => {
      observer.disconnect();
    };
  }, []);

  useEffect(() => {
    const frame: HTMLIFrameElement | null = iframeRef.current;
    if (!frame) return;
    try {
      if (frame.contentDocument?.readyState === "complete") {
        setFrameReady(true);
      }
    } catch {
      /* cross-origin — wait for onLoad */
    }
  }, []);

  const caption: string | undefined = slot.caption;
  const captionHref: string | undefined = slot.captionHref;

  return (
    <div className={`dvf-arch-block ${className}`}>
      <div
        className={frameReady ? "dvf-arch" : "dvf-arch dvf-arch-pending"}
        ref={boxRef}
      >
        <iframe
          ref={iframeRef}
          className={
            frameReady
              ? "dvf-arch-frame dvf-arch-frame-ready"
              : "dvf-arch-frame"
          }
          src={slot.src}
          title={slot.title}
          onLoad={() => setFrameReady(true)}
        />
      </div>
      {caption !== undefined && captionHref !== undefined ? (
        <a
          className="dvf-arch-caption"
          href={captionHref}
          target="_blank"
          rel="noreferrer"
        >
          <YouTubeLogo />
          <span>{caption}</span>
        </a>
      ) : null}
    </div>
  );
}
